import type {ResultPresentationBinding, ResultPresentationReceipt} from '@github-ui/ahp-relay/relay-result-presentation'
import type {BrowserObservationTime} from './browser-observation-clock'
import type {ResultPresentationCoverageReason} from '@github-ui/ahp-relay/result-presentation-health'
import type {UiReadinessRole, UiReadinessSurface, UiReadinessVisibility} from './ui-readiness-contract'

export const RESULT_PRESENTATION_EVENT = 'one_copilot.web.result_presentation'
export const RESULT_PRESENTATION_V1_POLICY = {
  policy_version: 'result_presentation_v1',
  gate_policy: 'one_copilot_browser_telemetry',
  sampling_policy: 'all_flagged_eligible_v1',
  eligibility_policy: 'live_receipt_milestones_v1',
  visibility_policy: 'continuous_elapsed_visible_witness_v1',
  content_taxonomy: 'answer_markdown_v1',
} as const
export const RESULT_PRESENTATION_POLICY = {
  policy_version: 'result_presentation_v2',
  gate_policy: RESULT_PRESENTATION_V1_POLICY.gate_policy,
  sampling_policy: RESULT_PRESENTATION_V1_POLICY.sampling_policy,
  eligibility_policy: RESULT_PRESENTATION_V1_POLICY.eligibility_policy,
  visibility_policy: 'uninterrupted_visible_elapsed_v2',
  content_taxonomy: RESULT_PRESENTATION_V1_POLICY.content_taxonomy,
} as const

export type ResultPresentationMethod = 'element_timing' | 'dom_visible_frame'
export type ResultPresentationUnknown =
  | 'hidden'
  | 'offscreen'
  | 'visibility_unsupported'
  | 'observer_failed'
  | 'paint_unsupported'
  | 'paint_unobserved'
  | 'stale_paint'
  | 'content_unobserved'
  | 'terminal_unobserved'
  | 'controls_unobserved'
  | 'stale_controls'
  | 'role_unknown'
  | 'binding_unknown'
  | 'disposed'
  | 'page_exit'
  | 'replaced'
  | 'observation_capacity'

export type ResultPresentationFailure = 'render_failed' | 'binding_mismatch'

export interface ResultPresentationProof extends ResultPresentationBinding {
  readonly turn_id: string
  readonly part_id?: string
  readonly terminal_state?: ResultPresentationReceipt['terminal_state']
  readonly witness_method: ResultPresentationMethod
  readonly witness_time_ms: number
  readonly clock_id: string
}

export interface ResultPresentationControls extends ResultPresentationBinding {
  readonly turn_id: string
  readonly role: Exclude<UiReadinessRole, 'unknown'>
  readonly control_scenario: 'settled_writable' | 'newer_active' | 'passive' | 'read_only'
  readonly active_turn_id?: string
  readonly action_mode: 'send' | 'steer' | 'queue' | 'blocked'
  readonly input_editable: boolean
  readonly send_enabled_for_valid_input: boolean
  readonly stop_present: boolean
}

export type ResultPresentationObservation =
  | {readonly observation: 'receipt'}
  | {
      readonly observation: 'presentation_observed'
      readonly witness_method: ResultPresentationMethod
      readonly presentation_coverage: 'confirmed_paint' | 'semantic_visibility_not_paint'
      readonly witness_time_ms: number
      readonly presentation_elapsed_ms: number
    }
  | ({
      readonly observation: 'controls_observed'
      readonly witness_method: 'dom_visible_frame'
      readonly presentation_coverage: 'semantic_visibility_not_paint'
      readonly witness_time_ms: number
      readonly controls_elapsed_ms: number
    } & Omit<ResultPresentationControls, keyof ResultPresentationBinding | 'turn_id'>)
  | {
      readonly observation: 'completed'
      readonly presentation_event_id: string
      readonly controls_event_id?: string
      readonly closure_time_ms: number
    }
  | {readonly observation: 'unresolved'; readonly reason: ResultPresentationUnknown}
  | {readonly observation: 'failed'; readonly reason: ResultPresentationFailure}

export type ResultPresentationFact = typeof RESULT_PRESENTATION_POLICY &
  Omit<ResultPresentationReceipt, 'observedAtMs' | 'monotonicMs' | 'visibility'> & {
    readonly client_view_id: string
    readonly event_id: string
    readonly receipt_event_id: string
    readonly clock_id: string
    readonly sequence: number
    readonly observed_at_ms: number
    readonly monotonic_ms: number
    readonly receipt_monotonic_ms: number
    readonly receipt_visibility: UiReadinessVisibility
    readonly surface: UiReadinessSurface
    readonly role: UiReadinessRole
    readonly visibility: UiReadinessVisibility
    readonly visibility_interrupted?: true
    readonly required_obligations: 'content_presentation' | 'terminal_controls'
  } & ResultPresentationObservation

export interface ResultPresentationMilestone {
  readonly receipt: ResultPresentationReceipt
  readonly closed: boolean
  presented(proof: ResultPresentationProof): void
  controls(controls: ResultPresentationControls): void
  unresolved(reason: ResultPresentationUnknown): void
  failed(reason: ResultPresentationFailure, time?: BrowserObservationTime): void
}

export type ResultPresentationCoverageFact = typeof RESULT_PRESENTATION_POLICY &
  Omit<ResultPresentationBinding, 'chat_id'> & {
    readonly observation: 'coverage_lost'
    readonly evidence_scope: 'source' | 'view'
    readonly reason: ResultPresentationCoverageReason
    readonly client_view_id?: string
    readonly event_id: string
    readonly source_event_id: string
    readonly clock_id: string
    readonly sequence: number
    readonly observed_at_ms: number
    readonly monotonic_ms: number
    readonly surface: UiReadinessSurface
    readonly role: UiReadinessRole
    readonly visibility: UiReadinessVisibility
    readonly suppressed_observations: number
  }

export type ResultPresentationEvent = ResultPresentationFact | ResultPresentationCoverageFact
